import React from "react";
import {
	ClassNameProps,
	Column,
	If,
	makeClassName,
	Modal,
	RelativeTimestamp,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { Reforge } from "@hex-insights/uploads";
import { NewBadge } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import { Image } from "../../../../Components/Image";
import {
	MediaLoadingPlaceholder,
	TextBlockLoadingPlaceholder,
	TextLineLoadingPlaceholder,
} from "../../../../Components/LoadingPlaceholder";
import { stripHTMLFromString } from "../../../../Components/RichText";
import { Newsletter, reforgeSizeMultiple } from "../../../../Utilities";
import { UserFormat } from "../../../Format";
import { NewsletterAdminModal } from "../AdminModal";
import { ModalNewsletter, NewsletterModal } from "../Modal";
import styles from "./styles.module.css";

const imageEdits: Reforge.ImageOptions["edits"] = {
	resize: { width: 350 * reforgeSizeMultiple },
};

export type CardNewsletter = ModalNewsletter & Pick<Newsletter, "imageKey" | "imageContentType">;

export type NewsletterCardProps = {
	newsletter: CardNewsletter;
	isNew?: boolean;
	variant?: "flat" | "tile";
	modalVariant?: "default" | "admin";
	withModalBackButton?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function NewsletterCard({
	newsletter,
	isNew = false,
	variant = "flat",
	modalVariant = "default",
	withModalBackButton,
	className,
	style,
}: NewsletterCardProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const ModalComponent = modalVariant === "admin" ? NewsletterAdminModal : NewsletterModal;

	return (
		<React.Fragment>
			<div
				onClick={openModal}
				className={makeClassName(styles["newsletter-card"], styles[`newsletter-card--${variant}`], className)}
				style={style}
			>
				<Column justify="spaced-start" verticalSpacing="0.625rem" style={{ flexGrow: 1 }}>
					<Column justify="spaced-start" verticalSpacing="0.375rem" style={{ flexGrow: 1 }}>
						<Column justify="spaced-start" verticalSpacing="0.625rem">
							<Image
								fileKey={newsletter.imageKey}
								contentType={newsletter.imageContentType}
								edits={imageEdits}
								alt={newsletter.title}
								sizing="cover"
								className={styles["newsletter-card__image"]}
							/>

							<Row justify="spaced-start" align="center" horizontalSpacing="0.375rem">
								<TruncatedText className={styles["newsletter-card__title"]}>{newsletter.title}</TruncatedText>

								<If condition={isNew}>
									<NewBadge size="small" />
								</If>
							</Row>
						</Column>

						<TruncatedText lines={2} className={styles["newsletter-card__preview"]}>
							{stripHTMLFromString(newsletter.body)}
						</TruncatedText>
					</Column>

					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center">
							{newsletter.author && (
								<span className={styles["newsletter-card__author"]}>{UserFormat.name(newsletter.author)}</span>
							)}
							{newsletter.releaseAt && (
								<RelativeTimestamp
									value={newsletter.releaseAt}
									className={styles["newsletter-card__timestamp"] + " timestamp"}
								/>
							)}
						</Row>

						<Icon.NewspaperClipping size={16} color="var(--theme-colors__teal)" />
					</Row>
				</Column>
			</div>

			<Modal.If condition={isModalOpen}>
				<ModalComponent newsletter={newsletter} withBackButton={withModalBackButton} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

export type NewsletterCardLoadingPlaceholderProps = Partial<ClassNameProps & StyleProps>;

export function NewsletterCardLoadingPlaceholder({ className, style }: NewsletterCardLoadingPlaceholderProps) {
	return (
		<div
			className={makeClassName(styles["newsletter-card"], styles["newsletter-card--loading"], className)}
			style={style}
		>
			<Column justify="spaced-start" verticalSpacing="0.625rem" style={{ flexGrow: 1 }}>
				<Column justify="spaced-start" verticalSpacing="0.375rem" style={{ flexGrow: 1 }}>
					<Column justify="spaced-start" verticalSpacing="0.625rem">
						<MediaLoadingPlaceholder className={styles["newsletter-card__image"]} />

						<TextLineLoadingPlaceholder />
					</Column>

					<TextBlockLoadingPlaceholder lines={2} className={styles["newsletter-card__preview"]} />
				</Column>

				<Row justify="spaced-start" align="center">
					<TextLineLoadingPlaceholder className={styles["newsletter-card__author"]} style={{ width: "10rem" }}>
						Example Name
					</TextLineLoadingPlaceholder>
					<TextLineLoadingPlaceholder className={styles["newsletter-card__timestamp"]} style={{ width: "4rem" }}>
						2 days ago
					</TextLineLoadingPlaceholder>
				</Row>
			</Column>
		</div>
	);
}
